import React, { forwardRef, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import classes from "./Description.module.css";
import { FaEdit } from "react-icons/fa";
import { FaRegCircleQuestion } from "react-icons/fa6";
import { ActionIcon, Button, Tooltip } from "@mantine/core";
import { notification } from "./util";
import { marked } from "marked";
import gql from "graphql-tag";
import { useLazyQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { useMediaQuery } from "@mantine/hooks";
import Editor from "./TextEditor";

const query = gql`
  query User($posId: Int) {
    whoami(posId: $posId) {
      canEdit
    }
  }
`;

const Description = ({ text, fileName, centered }) => {
  const { user, config, language } = useSelector((state) => ({
    user: state.oidc.user,
    config: state.config,
    language: state.language,
  }));
  const { posId } = config;

  const [loadUser, { data }] = useLazyQuery(query, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    let attempts = 0;
    const fetchWithRetry = async () => {
      const res = await loadUser({ variables: { posId } });
      if (res.data?.whoami?.canEdit === false && attempts < 2) {
        attempts++;
        setTimeout(fetchWithRetry, 300);
      }
    };

    fetchWithRetry();
  }, [posId, loadUser]);

  const [view, setView] = useState("normal");
  const [localText, setLocalText] = useState(text);
  const [dirty, setDirty] = useState(false);

  const canEdit = data?.whoami?.canEdit;

  useEffect(() => {
    console.log("user", user);
    console.log("canEdit:", canEdit);
  }, [user, canEdit]);

  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    setLocalText(text);
  }, [text]);

  const handleCancel = () => {
    setLocalText(text);
    setView("normal");
  };

  const handlePreview = () => {
    setView(view === "edit" ? "preview" : "edit");
  };

  const handleSubmit = () => {
    const token = user ? user.access_token : null;

    const descriptionFolder =
      config.descriptionFolderUrl[0] === "/"
        ? config.descriptionFolderUrl.slice(1)
        : config.descriptionFolderUrl;

    fetch(
      `${config.apiUri}/config/edit?posId=${config.posId}&filename=${descriptionFolder}/${fileName}`,
      {
        method: "POST",
        body: localText,
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
      }
    )
      .then((response) => {
        if (response.status === 200) {
          setView("normal");
          notification("success", language.SaveTextSuccess);
        } else {
          notification("error", language.ErrorMsgSaveText);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const ButtonsContainer = () => {
    return (
      <div className={classes.editorButtonContainer}>
        <Button
          onClick={handleCancel}
          className={`${classes.cancelButton}`}
          variant="outline"
          size={isMobile ? "sm" : "md"}
          radius="sm"
          color="indigo"
        >
          {language.Cancel}
        </Button>
        <Button
          onClick={handlePreview}
          className={`mantine-Secondary-btn ${classes.cancelButton}`}
          variant="filled"
          size={isMobile ? "sm" : "md"}
          radius="sm"
          color="indigo"
        >
          {view === "edit"
            ? isMobile
              ? language.Show
              : language.TextEditor.Button_Preview
            : language.TextEditor.Button_Edit}
        </Button>
        <Button
          onClick={handleSubmit}
          className={`mantine-Primary-btn ${classes.cancelButton}`}
          variant="filled"
          size={isMobile ? "sm" : "md"}
          radius="sm"
          color="indigo"
          disabled={!dirty}
        >
          {language.Save}
        </Button>
      </div>
    );
  };

  const TooltipInfoComponent = forwardRef((props, ref) => (
    <div ref={ref} {...props}>
      <FaRegCircleQuestion
        className={`${classes.tooltipInfoIcon} text__editor--tooltip-info-icon`}
      />
    </div>
  ));

  const TooltipEditComponent = forwardRef((props, ref) => (
    <div ref={ref} {...props}>
      <FaEdit
        className={`${classes.editIcon} text__editor--tooltip-edit-icon`}
      />
    </div>
  ));

  if (!text || text.length < 1) return <div />;

  if (!canEdit || !user)
    return (
      <>
        <ReactMarkdown
          className={`${classes.description} description ${
            centered ? classes.marginAuto : ""
          }`}
          rehypePlugins={[rehypeRaw]}
        >
          {localText}
        </ReactMarkdown>
      </>
    );

  return (
    <div
      className={`${classes.descriptionContainer} ${
        centered ? classes.centered : ""
      }`}
    >
      {view === "normal" && (
        <div>
          <ReactMarkdown
            className={`${classes.description} description ${
              centered ? classes.marginAuto : ""
            }`}
            rehypePlugins={[rehypeRaw]}
          >
            {localText}
          </ReactMarkdown>
          <Tooltip
            classNames={{ tooltip: "tooltip-body" }}
            openDelay={500}
            transitionProps={{ duration: 200 }}
            events={{ hover: true, focus: true, touch: true }}
            label={language.TextEditor.Button_Edit}
          >
            <ActionIcon
              className={classes.editIconContainer}
              variant="subtle"
              styles={{
                icon: {
                  color: "black",
                },
              }}
              aria-label="Edit"
              onClick={() => setView("edit")}
            >
              <TooltipEditComponent />
            </ActionIcon>
          </Tooltip>
        </div>
      )}
      {view === "edit" && (
        <>
          <div className={classes.headerContainer}>
            <h1 className={classes.editorHeader}>
              {language.TextEditor.TextEditor}
            </h1>
            <Tooltip
              w={220}
              classNames={{ tooltip: "tooltip-body" }}
              transitionProps={{ duration: 200 }}
              multiline
              events={{ hover: true, focus: true, touch: true }}
              label={language.TextEditor.HelpText}
            >
              <TooltipInfoComponent />
            </Tooltip>
          </div>
          <div className={classes.editorContainer}>
            <Editor
              className={classes.editor}
              value={marked(localText)}
              onChange={setLocalText}
              setDirty={setDirty}
            />
          </div>
          <ButtonsContainer />
        </>
      )}
      {view === "preview" && (
        <>
          <ReactMarkdown
            className={`${classes.description} description ${
              centered ? classes.marginAuto : ""
            }`}
            rehypePlugins={[rehypeRaw]}
          >
            {localText}
          </ReactMarkdown>
          <ButtonsContainer />
        </>
      )}
    </div>
  );
};

export default Description;
